import { useEffect, useRef, useState, type RefObject } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  updateInvestigationReport,
  type IInvestigationReport,
} from '@apis/investigationReports';

export interface UseEditableReportTitleInput {
  siteId: string;
  reportId: string;
  record: IInvestigationReport | null | undefined;
  defaultName: string;
  defaultDescription: string;
}

export interface UseEditableReportTitleResult {
  name: string;
  description: string;
  titleRef: RefObject<HTMLHeadingElement | null>;
  descRef: RefObject<HTMLParagraphElement | null>;
  handleTitleBlur: () => void;
  handleDescBlur: () => void;
  revertTitle: () => void;
  revertDesc: () => void;
}

/**
 * Manages the editable report title + description block that is shared
 * across all analytics report pages (DailyRoute, ObserveLocation,
 * WorkerTimesheet, ContractorAnalysis).
 *
 * Design:
 *   - Local `name` / `description` state is seeded exactly once from the
 *     archived record (on first truthy `record`). The `seededRef` guard
 *     prevents background refetches from clobbering in-flight edits.
 *   - On blur, the handler PUTs the trimmed text to the record and
 *     reflects the server's canonical value (trim, sanitisation,
 *     defaults) back into local state.
 *   - #12 fix: `defaultName` and `defaultDescription` are mirrored into
 *     `useRef`s updated each render. The `onSuccess` callback reads from
 *     those refs instead of from the closure, avoiding stale i18n strings
 *     when the language flips during an in-flight mutation.
 *   - Uses `useRef` over `useEffectEvent` because `useEffectEvent` is
 *     still experimental in React 19.
 */
export function useEditableReportTitle({
  siteId,
  reportId,
  record,
  defaultName,
  defaultDescription,
}: UseEditableReportTitleInput): UseEditableReportTitleResult {
  const queryClient = useQueryClient();

  const [name, setName] = useState<string>(defaultName);
  const [description, setDescription] = useState<string>(defaultDescription);

  // Seed local state from the record exactly once, on initial load.
  // Background refetches (window-focus, reconnect, mutation invalidation)
  // re-run this effect with a fresh `record` reference; without the
  // `seededRef` guard, those refetches would clobber the user's
  // in-flight edits in the contentEditable header. Server-side
  // normalisation after a successful PUT is reflected explicitly in
  // `updateMutation.onSuccess` instead of via this effect.
  const seededRef = useRef(false);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (!record || seededRef.current) return;
    // Slow-network race: if the user started typing in the contentEditable
    // *before* the record query resolved, seeding now would clobber the
    // in-progress DOM edit. Skip in that case and let the next paint
    // (after the user blurs) re-enter this effect — by then `seededRef`
    // will still be `false` and seeding will proceed safely against an
    // unfocused field.
    const activeEl = typeof document !== 'undefined' ? document.activeElement : null;
    if (activeEl === titleRef.current || activeEl === descRef.current) return;
    seededRef.current = true;
    setName(record.title || defaultName);
    setDescription(record.description || defaultDescription);
  }, [record, defaultName, defaultDescription]);

  // #12: Mirror the current i18n defaults into refs so the `onSuccess`
  // callback is always reading the latest language's strings, even when
  // the language flips while a mutation is still in flight. The ref
  // updates run in a passive effect (not during render) to satisfy
  // react-hooks/refs in React 19. The window between commit and the
  // effect can only be hit by a synchronous mutation resolution, which
  // is impossible here (the mutation is a network call).
  const defaultNameRef = useRef(defaultName);
  const defaultDescriptionRef = useRef(defaultDescription);
  useEffect(() => {
    defaultNameRef.current = defaultName;
    defaultDescriptionRef.current = defaultDescription;
  });

  const updateMutation = useMutation({
    mutationFn: (patch: { title?: string; description?: string }) =>
      updateInvestigationReport(siteId, reportId, patch),
    onSuccess: (updated) => {
      queryClient.setQueryData(
        ['investigationReport', siteId, reportId],
        updated,
      );
      // Reflect server-side normalisation (trim, sanitisation, defaults)
      // back into local state. The seed-once effect won't re-fire after
      // the cache update, so this is the only path that lets the
      // server's canonical value reach the contentEditable nodes.
      // Read from refs (not closure) so language flips mid-mutation
      // use the current translation strings.
      setName(updated.title || defaultNameRef.current);
      setDescription(updated.description || defaultDescriptionRef.current);
      void queryClient.invalidateQueries({
        queryKey: ['investigationReports', siteId],
      });
    },
  });

  const handleTitleBlur = () => {
    const next = titleRef.current?.textContent?.trim() || defaultName;
    if (next === name) {
      if (titleRef.current && titleRef.current.textContent !== next) {
        titleRef.current.textContent = next;
      }
      return;
    }
    setName(next);
    updateMutation.mutate({ title: next });
  };

  const handleDescBlur = () => {
    const next = descRef.current?.textContent?.trim() || defaultDescription;
    if (next === description) {
      if (descRef.current && descRef.current.textContent !== next) {
        descRef.current.textContent = next;
      }
      return;
    }
    setDescription(next);
    updateMutation.mutate({ description: next });
  };

  const revertTitle = () => {
    if (titleRef.current) titleRef.current.textContent = name;
    titleRef.current?.blur();
  };

  const revertDesc = () => {
    if (descRef.current) descRef.current.textContent = description;
    descRef.current?.blur();
  };

  return {
    name,
    description,
    titleRef,
    descRef,
    handleTitleBlur,
    handleDescBlur,
    revertTitle,
    revertDesc,
  };
}
